import tourCopy from './drsTourGuide.json';

export const TOUR_SELECTORS = {
  title: '#dynamic-reports-title',
  categoryTabs: '#dynamic-reports-category-tabs',
  reportType: 's-select[input-id="reportType"]',
  companySelect: '#Company\\ code',
  /** Attribute selector so id with space matches the input; ensures focus targets the correct element */
  invoiceNumberSelect: 'input[id="Invoice number"]',
  submit: '#dynamic-reports-submit',
  exportAll: '[data-testid="export-all-dynamic-reports"]',
  transactionTrigger: '#dynamic-reports-tab-transaction-trigger',
  changesLogTrigger: '#dynamic-reports-report-changes-log-trigger',
  /** Trigger that selects tour report (invoiceLines if present, else changesLogV2) */
  tourReportTrigger: '#dynamic-reports-report-tour-trigger',
  completeTrigger: '#dynamic-reports-tour-complete-trigger'
};

const safeCategories = (categories) => (Array.isArray(categories) ? categories : []);

export const getTransactionCategoryId = (categories) => {
  const validCategories = safeCategories(categories);
  const byId = validCategories.find(category => category?.id === 'transaction');
  return byId?.id || validCategories[0]?.id;
};

export const getChangesLogReportId = (categories, transactionCategoryId) => {
  const validCategories = safeCategories(categories);
  const category = validCategories.find(item => item?.id === transactionCategoryId) || validCategories[0];
  const reports = category?.reports || [];
  const byId = reports.find(report => report?.id === 'changesLogV2');
  return byId?.id || reports[0]?.id;
};

/**
 * Tour report preference: invoiceLines if present in transaction category, else changesLogV2.
 * Used for report dropdown selection and submit-step target in the tour.
 */
export const getTourReportId = (categories, transactionCategoryId) => {
  const validCategories = safeCategories(categories);
  const category = validCategories.find(item => item?.id === transactionCategoryId) || validCategories[0];
  const reports = category?.reports || [];
  const invoiceLines = reports.find(report => report?.id === 'invoiceLines');
  if (invoiceLines) return invoiceLines.id;
  const changesLog = reports.find(report => report?.id === 'changesLogV2');
  return changesLog?.id || reports[0]?.id;
};

const waitConditions = (waitTimeout, skipStepIfTargetMissing = false) => ({
  waitForElementConditions: {
    waitTimeout,
    skipStepIfTargetMissing
  }
});

const navButtons = {
  back: { text: tourCopy.steps.buttons.back, tourActions: [{ type: 'back' }], classes: 'tertiary' },
  next: { text: tourCopy.steps.buttons.next, tourActions: [{ type: 'next' }], classes: 'primary' },
  exit: { text: tourCopy.steps.buttons.exit, tourActions: [{ type: 'cancel' }], classes: 'ghost' },
  start: { text: 'Start', tourActions: [{ type: 'next' }], classes: 'primary' },
  selectReportCategory: {
    text: tourCopy.steps.buttons.selectReportCategory,
    tourActions: [
      { type: 'domEvent', eventName: 'click', selector: TOUR_SELECTORS.transactionTrigger },
      { type: 'next' }
    ],
    classes: 'primary'
  },
  selectFilters: {
    text: tourCopy.steps.buttons.selectFilters,
    tourActions: [
      { type: 'domEvent', eventName: 'click', selector: TOUR_SELECTORS.tourReportTrigger },
      { type: 'next' }
    ],
    classes: 'primary'
  },
  submit: {
    text: tourCopy.steps.buttons.submit,
    tourActions: [
      { type: 'domEvent', eventName: 'click', selector: TOUR_SELECTORS.submit },
      { type: 'next' }
    ],
    classes: 'primary'
  },
  exportAll: {
    text: tourCopy.steps.buttons.exportAll,
    tourActions: [
      { type: 'domEvent', eventName: 'click', selector: TOUR_SELECTORS.exportAll },
      { type: 'domEvent', eventName: 'click', selector: TOUR_SELECTORS.completeTrigger },
      { type: 'complete' }
    ],
    classes: 'primary'
  }
};

export const buildDynamicReportsTour = (categories, transactionCategoryId) => {
  return [
    {
      id: 'dynamic-reports-tour',
      visual: {
        enableHighlightOverlay: true
      },
      exit: {
        requireConfirmation: true,
        confirmationMessage: 'Are you sure you want to exit the tour?'
      },
      stepDefaults: {
        navigation: {
          buttons: [
            { text: tourCopy.steps.buttons.back, tourActions: [{ type: 'back' }], classes: 'tertiary' },
            { text: tourCopy.steps.buttons.next, tourActions: [{ type: 'next' }], classes: 'primary' },
            { text: tourCopy.steps.buttons.exit, tourActions: [{ type: 'cancel' }], classes: 'ghost' },
          ],
          cancelIcon: { enabled: true }
        },
        behavior: {
          scrollToTarget: true,
          ...waitConditions(3000)
        },
        showProgressIndicator: true
      },
      steps: [
        {
          id: 'dynamic-reports-welcome',
          content: tourCopy.steps.welcome,
          behavior: {
            scrollToTarget: true
          },
          navigation: {
            buttons: [navButtons.start, navButtons.exit],
            cancelIcon: { enabled: true }
          },
          target: {
            selector: TOUR_SELECTORS.title
          }
        },
        {
          id: 'dynamic-reports-data-element',
          content: tourCopy.steps.transaction,
          behavior: {
            scrollToTarget: true
          },
          target: {
            selector: TOUR_SELECTORS.categoryTabs,
            placement: 'bottom'
          },
          navigation: {
            buttons: [navButtons.back, navButtons.selectReportCategory, navButtons.exit],
            cancelIcon: { enabled: true }
          }
        },
        {
          id: 'dynamic-reports-report-select',
          content: tourCopy.steps.reportSelect,
          target: {
            selector: TOUR_SELECTORS.reportType,
            placement: 'bottom'
          },
          behavior: {
            scrollToTarget: true
          },
          navigation: {
            buttons: [navButtons.back, navButtons.selectFilters, navButtons.exit],
            cancelIcon: { enabled: true }
          }
        },
        {
          id: 'dynamic-reports-submit',
          content: tourCopy.steps.generate,
          behavior: {
            scrollToTarget: true
          },
          target: {
            selector: TOUR_SELECTORS.invoiceNumberSelect ,
            placement: 'bottom'
          },
          navigation: {
            buttons: [navButtons.back, navButtons.submit, navButtons.exit],
            cancelIcon: { enabled: true }
          },
          showProgressIndicator: false
        },
        {
          id: 'dynamic-reports-export',
          content: tourCopy.steps.export,
          target: {
            selector: TOUR_SELECTORS.exportAll,
            placement: 'right'
          },
          behavior: {
            scrollToTarget: true,
            // Wait up to 60 seconds for export button to appear (data loading can take time)
            ...waitConditions(60000, true)
          },
          navigation: {
            buttons: [navButtons.back, navButtons.exportAll],
            cancelIcon: { enabled: true }
          },
          showProgressIndicator: false
        }
      ]
    }
  ];
};
